import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileText, Grid3X3, Eye, Flag,
  Settings, LogOut, ChevronLeft, ChevronRight, ChevronDown, Shield,
} from "lucide-react";
import SettingsModal from "@/components/SettingsModal";
import { isAdminEmail } from "@/config/admin";
import { API } from "@/App";

export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(
    () => localStorage.getItem("wokelo_nav_collapsed") === "true"
  );
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    // Quick client-side check first
    const username = localStorage.getItem("wokelo_username") || "";
    if (isAdminEmail(username)) {
      setIsAdmin(true);
      return;
    }
    // Backend check for dynamic admins
    fetch(`${API}/admin/check`)
      .then(r => r.json())
      .then(data => { if (data.is_admin) setIsAdmin(true); })
      .catch(() => {});
  }, []);

  const NAV_ITEMS = [
    {
      icon: FileText,
      label: "News Feed",
      path: "/briefing",
      children: [
        { label: "Portfolio", path: "/briefing" },
        { label: "Watchlist", path: "/watchlist" },
      ],
    },
    { icon: Grid3X3, label: "Portfolio Matrix", path: "/portfolio-matrix" },
    { icon: Eye,      label: "Watchlist Matrix",   path: "/watchlist-matrix" },
    { icon: Flag,     label: "Flagged Companies",  path: "/flagged-companies" },
    ...(isAdmin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  // Track which accordion group is open (by label). Default: expand "News Feed".
  const [expandedGroup, setExpandedGroup] = useState("News Feed");

  const toggleNav = () => {
    const next = !navCollapsed;
    setNavCollapsed(next);
    localStorage.setItem("wokelo_nav_collapsed", String(next));
  };

  const sidebarW = navCollapsed ? 48 : 210;
  const [windowW, setWindowW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handle = () => setWindowW(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  const effectiveSidebarW  = windowW < 768 ? 48 : sidebarW;
  const effectiveCollapsed = windowW < 768 ? true : navCollapsed;

  return (
    <div className="flex h-screen" style={{ background: "#FFFFFF" }}>
      {/* ── Fixed dark sidebar ───────────────────────────────────── */}
      <aside
        className="fixed left-0 top-0 bottom-0 flex flex-col z-50 overflow-hidden"
        style={{
          width: effectiveSidebarW,
          background: "#181B20",
          borderRight: "1px solid #212529",
          transition: "width 200ms ease-in-out",
        }}
      >
        {/* Logo + collapse toggle */}
        <div
          className="flex-shrink-0 flex items-center"
          style={{
            padding: "13px 8px",
            borderBottom: "1px solid #212529",
            justifyContent: effectiveCollapsed ? "center" : "space-between",
          }}
        >
          {!effectiveCollapsed && (
            <img src="/wokelo-logo-white.svg" alt="Wokelo" className="h-6 ml-2" />
          )}
          <button
            onClick={toggleNav}
            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
            style={{ color: "#969EA5" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#222528"; e.currentTarget.style.color = "#F5F5F5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#969EA5"; }}
            data-testid="nav-panel-toggle"
            title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          >
            {navCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-2 pt-3 overflow-hidden">
          {NAV_ITEMS.map(({ icon: Icon, label, path, children, comingSoon }) => {
            const isActive    = path && (location.pathname.startsWith(path) || (label === "News Feed" && (location.pathname === "/watchlist" || location.pathname.startsWith("/story/"))));
            const isExpanded  = expandedGroup === label;
            const hasChildren = Boolean(children?.length);

            const handleParentClick = () => {
              if (comingSoon) return;
              if (hasChildren) {
                // Toggle accordion; also navigate to the parent route
                setExpandedGroup(isExpanded ? null : label);
                if (path) navigate(path);
              } else if (path) {
                navigate(path);
              }
            };

            return (
              <div key={label}>
                {/* Parent row */}
                <button
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={handleParentClick}
                  title={effectiveCollapsed ? label : comingSoon ? `${label} — coming soon` : undefined}
                  className="w-full flex items-center rounded-lg text-sm font-medium transition-all mb-0.5 relative"
                  style={{
                    gap: effectiveCollapsed ? 0 : 10,
                    padding: effectiveCollapsed ? "10px 0" : "9px 12px",
                    justifyContent: effectiveCollapsed ? "center" : "flex-start",
                    background: isActive && !hasChildren ? "#272B2E" : isActive ? "transparent" : "transparent",
                    color: comingSoon ? "#4A5260" : isActive ? "#97C8A6" : "#969EA5",
                    cursor: comingSoon ? "default" : "pointer",
                  }}
                  onMouseEnter={e => {
                    if (comingSoon) return;
                    if (!isActive) {
                      e.currentTarget.style.background = "#222528";
                      e.currentTarget.style.color = "#F5F5F5";
                    }
                  }}
                  onMouseLeave={e => {
                    if (comingSoon) return;
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#969EA5";
                    }
                  }}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {!effectiveCollapsed && (
                    <>
                      <span className="truncate flex-1 text-left">{label}</span>
                      {comingSoon && (
                        <span style={{ fontSize: 9, color: "#3D4553", fontWeight: 500, letterSpacing: "0.04em" }}>
                          SOON
                        </span>
                      )}
                      {hasChildren && (
                        <ChevronDown
                          size={12}
                          className="flex-shrink-0 transition-transform duration-200"
                          style={{
                            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                            color: "#4A5260",
                          }}
                        />
                      )}
                    </>
                  )}
                </button>

                {/* Sub-items (accordion — hidden when collapsed sidebar or group closed) */}
                {!effectiveCollapsed && hasChildren && isExpanded && (
                  <div className="mb-1" style={{ paddingLeft: "12px" }}>
                    {children.map((child) => {
                      let childActive = child.path && location.pathname === child.path;
                      // When viewing a story, highlight the feed the user came from
                      if (!childActive && location.pathname.startsWith("/story/")) {
                        const lastFeed = sessionStorage.getItem("wokelo_last_feed");
                        if (lastFeed === "portfolio" && child.path === "/briefing") childActive = true;
                        if (lastFeed === "watchlist" && child.path === "/watchlist") childActive = true;
                      }
                      return (
                        <button
                          key={child.label}
                          data-testid={`nav-sub-${child.label.toLowerCase()}`}
                          onClick={() => !child.comingSoon && child.path && navigate(child.path)}
                          className="w-full flex items-center gap-2.5 rounded-md text-sm transition-colors mb-0.5"
                          style={{
                            padding: "6px 10px",
                            color: child.comingSoon ? "#3D4553" : childActive ? "#97C8A6" : "#636B75",
                            cursor: child.comingSoon ? "default" : "pointer",
                            background: childActive ? "#272B2E" : "transparent",
                          }}
                          onMouseEnter={e => {
                            if (child.comingSoon) return;
                            if (!childActive) {
                              e.currentTarget.style.background = "#1E2126";
                              e.currentTarget.style.color = "#C8CDD2";
                            }
                          }}
                          onMouseLeave={e => {
                            if (child.comingSoon) return;
                            if (!childActive) {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.color = "#636B75";
                            }
                          }}
                        >
                          {/* Bullet dot */}
                          <span
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background: child.comingSoon ? "#3D4553" : childActive ? "#97C8A6" : "#636B75",
                            }}
                          />
                          <span className="truncate flex-1 text-left">{child.label}</span>
                          {child.comingSoon && (
                            <span style={{ fontSize: 9, color: "#3D4553", fontWeight: 500 }}>SOON</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="p-2 flex-shrink-0 overflow-hidden" style={{ borderTop: "1px solid #212529" }}>
          <button
            data-testid="nav-settings"
            onClick={() => setSettingsOpen(true)}
            title={effectiveCollapsed ? "Settings" : undefined}
            className="w-full flex items-center rounded-lg text-sm transition-colors mb-1"
            style={{
              gap: effectiveCollapsed ? 0 : 10,
              padding: effectiveCollapsed ? "9px 0" : "9px 12px",
              justifyContent: effectiveCollapsed ? "center" : "flex-start",
              color: "#969EA5",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#222528"; e.currentTarget.style.color = "#F5F5F5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#969EA5"; }}
          >
            <Settings size={16} className="flex-shrink-0" />
            {!effectiveCollapsed && <span className="truncate text-left">Settings</span>}
          </button>
          <button
            data-testid="nav-logout"
            onClick={onLogout}
            title={effectiveCollapsed ? "Sign Out" : undefined}
            className="w-full flex items-center rounded-lg text-sm transition-colors"
            style={{
              gap: effectiveCollapsed ? 0 : 10,
              padding: effectiveCollapsed ? "9px 0" : "9px 12px",
              justifyContent: effectiveCollapsed ? "center" : "flex-start",
              color: "#969EA5",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#222528"; e.currentTarget.style.color = "#F5F5F5"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#969EA5"; }}
          >
            <LogOut size={16} className="flex-shrink-0" />
            {!effectiveCollapsed && <span className="truncate text-left">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main area — margin tracks sidebar width */}
      <div
        className="flex flex-col flex-1 min-w-0"
        style={{
          marginLeft: effectiveSidebarW,
          transition: "margin-left 200ms ease-in-out",
        }}
      >
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onLogout={onLogout}
      />
    </div>
  );
}
